import { Component, OnInit, ChangeDetectorRef, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { HeaderComponent } from '../header/header.component';
import { UserGroupsService } from '../../services/usergroups.service';
import { UsersService } from '../../services/users.service';
import { AuthService } from '../../services/auth.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-usergroups',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, HeaderComponent],
  templateUrl: './usergroups.component.html',
  styleUrls: []
})
export class UserGroupsComponent implements OnInit {
  copyrightDetails: any;

  // --- Loading / UI State ---
  isLoaded = false;
  isRefreshTable = false;
  refreshTableText = '';
  totalRecordsText = '';
  totalTimeText = '';
  isProcessed = true;
  isUsersLoaded = true;
  rowsInfo = '';

  // --- Groups Table ---
  groupList: any[] = [];
  totalRecords = 0;
  sortType = 'name';
  sortReverse = false;
  search = '';
  currentPage = 1;
  pageSize = 10;
  selectedRow: number | null = null;

  // --- Column Visibility ---
  isColumnSettingsVisible = false;
  columnVisibility: { [key: string]: boolean } = {
    name: true,
    addChannel: true,
    addUsers: true
  };

  // --- Create / Update Group Panel ---
  isShowGroup = false;
  isCreateGroup = false;
  group: any = {};
  oldGroupDetails: any = {};
  saveBtnText = 'Save';
  saveBtnError = false;
  isSaveSuccess = false;
  updateBtnText = 'Update';
  updateBtnError = false;
  isUpdateSuccess = false;
  isConfirmDelete = false;
  isDeleteSuccess = false;

  // --- Group Users Panel ---
  isShowUserGroup = false;
  userGroupId: string | number = '';
  usersList: any[] = [];
  totalUserRecords = 0;
  linkUserList: any[] = [];
  assignedUser: any = {};
  searchUser = '';
  userSortType = 'name';
  userSortReverse = false;

  // --- Group Channels Panel ---
  isShowUserChannel = false;
  searchChannel = '';

  // --- Messages ---
  message: string | null = null;
  errorMessage: string | null = null;

  private platformId = inject(PLATFORM_ID);

  constructor(
    private userGroupsService: UserGroupsService,
    private usersService: UsersService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef
  ) { }

  async ngOnInit(): Promise<void> {

    
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    if (typeof window !== 'undefined') {
      const savedDetails = sessionStorage.getItem('copyrightDetails');
      if (savedDetails) {
        this.copyrightDetails = JSON.parse(savedDetails);
      }
    }

    // Wait for auth to be ready before loading data
    try {
      await firstValueFrom(
        toObservable(this.authService.user).pipe(
          filter(u => !!u)
        )
      );
      this.getGroupList(undefined, false);
    } catch {
      this.getGroupList(undefined, false);
    }
  }


  // Add these to your Group Channels Panel section
channelList: any[] = [
  { id: 1, name: 'Channel name one' },
  { id: 2, name: 'Channel name two' }
];


getFilteredChannels(): any[] {
  const term = this.searchChannel.toLowerCase().trim();
  if (!term) return this.channelList;

  return this.channelList.filter(c => 
    c.name && c.name.toLowerCase().includes(term)
  );
}

  // ========================================================================
  // GROUPS TABLE
  // ========================================================================

  getGroupList(refresh?: boolean, isCache?: boolean): void {

    if (refresh) {
 this.columnVisibility = {
    name: true,
    addChannel: true,
    addUsers: true
  };
      this.searchChannel='';
      this.searchUser='';
      this.search = '';
      this.totalRecords = 0;
      this.isRefreshTable = true;
      this.refreshTableText = 'Table is refreshing...';
      this.currentPage = 1;  
    }

    this.isLoaded = false;

    this.userGroupsService.GetGroup()
      .then((response: any) => {
        const data = Array.isArray(response) ? response : (response.data || response);
        this.groupList = data;
        this.totalRecords = this.groupList.length;
        this.isLoaded = true;

        if (refresh) {
          this.totalRecordsText = 'record(s) loaded';
          this.refreshTableText = 'Successfully refreshed';
          setTimeout(() => {
            this.isRefreshTable = false;
            this.cdr.detectChanges();
          }, 3500);
        }

        this.updateTableInformation(1);
        this.cdr.detectChanges();
      })
      .catch((error: any) => {
        if (refresh) {
          this.isRefreshTable = true;
          this.refreshTableText = 'Unsuccessful!';
        }
        setTimeout(() => {
          this.isRefreshTable = false;
          this.cdr.detectChanges();
        }, 2500);
        console.error('Error loading groups:', error);
      });
  }

  // --- Sorting ---
  setSort(type: string): void {
    if (this.sortType === type) {
      this.sortReverse = !this.sortReverse;
    } else {
      this.sortType = type;
      this.sortReverse = false;
    }
  }

  getFilteredGroups(): any[] {
    let filtered = this.groupList;
    if (this.search) {
      const term = this.search.toLowerCase();
      filtered = filtered.filter((g: any) =>
        g.name && g.name.toLowerCase().includes(term)
      );
    }
    filtered = [...filtered].sort((a: any, b: any) => {
      const valA = a[this.sortType] ? String(a[this.sortType]).toLowerCase() : '';
      const valB = b[this.sortType] ? String(b[this.sortType]).toLowerCase() : '';
      if (valA < valB) return this.sortReverse ? 1 : -1;
      if (valA > valB) return this.sortReverse ? -1 : 1;
      return 0;
    });
    return filtered;
  }

  getPaginatedGroups(): any[] {
    const filtered = this.getFilteredGroups();
    const startIndex = (this.currentPage - 1) * this.pageSize;
    return filtered.slice(startIndex, startIndex + this.pageSize);
  }

  getTotalFilteredCount(): number {
    return this.getFilteredGroups().length;
  }

  getTotalPages(): number {
    return Math.ceil(this.getTotalFilteredCount() / this.pageSize);
  }

  getVisibleCount(): number {
    return Math.min(this.currentPage * this.pageSize, this.getTotalFilteredCount());
  }

  pageChange(page: number): void {
    this.currentPage = page;
    this.updateTableInformation(page);
  }

  updateTableInformation(currentPage: number): void {
    const total = this.getTotalFilteredCount();
    if (total === 0) {
      this.rowsInfo = 'Displaying 0 Records';
      return;
    }
    if (currentPage === 1) {
      this.rowsInfo = `Displaying 1-${Math.min(total, this.pageSize)} Of ${total} Records`;
    } else {
      const start = (currentPage - 1) * this.pageSize + 1;
      const end = Math.min(currentPage * this.pageSize, total);
      this.rowsInfo = `Displaying ${start}-${end} Of ${total} Records`;
    }
  }

  // --- Row Selection ---
  setClickedRow(index: number): void {
    this.selectedRow = index;
  }

  // --- Column Settings ---
  showHideColumnSettings(): void {
    this.isColumnSettingsVisible = !this.isColumnSettingsVisible;
  }

  // ========================================================================
  // CREATE GROUP PANEL
  // ========================================================================

  openForm(): void {
    this.isShowGroup = true;
    this.isCreateGroup = true;
    this.resetForm();
  }

  closeForm(): void {
    this.isShowGroup = false;
    this.saveBtnText = 'Save';
    this.saveBtnError = false;
    setTimeout(() => {
      this.isSaveSuccess = false;
      this.isUpdateSuccess = false;
      this.cdr.detectChanges();
    }, 500);
  }

  resetForm(): void {
    this.saveBtnText = 'Save';
    this.saveBtnError = false;
    this.isSaveSuccess = false;
    this.updateBtnText = 'Update';
    this.updateBtnError = false;
    this.isUpdateSuccess = false;
    this.isConfirmDelete = false;
    this.isDeleteSuccess = false;
    this.group = { name: null };
    this.message = null;
  }

  createAnotherForm(): void {
    this.group = {};
    this.isCreateGroup = true;
    this.isSaveSuccess = false;
  }

  isGroupFormInvalid(): boolean {
    return !this.group.name || this.group.name.trim().length === 0
      ||
      this.group.name.length < 2 ||
      this.group.name.length > 40;
  }

  saveGroup(group: any): void {
    this.saveBtnText = 'Saving Now...';
    this.isProcessed = false;

    this.userGroupsService.CreateGroup(group)
      .then((response: any) => {
        this.saveBtnText = 'Save';
        this.isProcessed = true;
        this.isSaveSuccess = true;
        group.id = response.data?.inserted_id ?? response.inserted_id;
        this.groupList.push({ ...group });
        this.totalRecords = this.groupList.length;
        this.updateTableInformation(this.currentPage);
        this.autoDismissMessages();
        this.cdr.detectChanges();
      })
      .catch((error: any) => {
        this.isProcessed = true;
        if (error.status === 409) {
          this.message = 'Record Already Exist';
        } else {
          this.message = 'Error while creating group!! Try another Group name.';
        }
        this.saveBtnText = 'Oops! Something went wrong';
        this.saveBtnError = true;
        setTimeout(() => {
          this.saveBtnText = 'Save';
          this.saveBtnError = false;
          this.message = null;
          this.cdr.detectChanges();
        }, 3500);
      });
  }

  // ========================================================================
  // UPDATE / DELETE GROUP PANEL
  // ========================================================================

  dblClickAction(group_information: any): void {
    this.message = null;
    this.errorMessage = null;
    this.isShowGroup = true;
    this.isCreateGroup = false;
    this.isSaveSuccess = false;
    this.isUpdateSuccess = false;
    this.isDeleteSuccess = false;
    this.isConfirmDelete = false;
    this.group = { ...group_information };
    this.oldGroupDetails = { ...group_information };
  }

  hasUpdateChanges(payload: any): boolean {
    return this.oldGroupDetails.name !== payload.name;
  }

  updateGroup(group: any): void {
    this.updateBtnText = 'Updating Now...';
    this.isProcessed = false;

    if (!this.hasUpdateChanges(group)) {
      this.updateBtnText = 'Nothing to update.';
      this.updateBtnError = true;
      setTimeout(() => {
        this.isProcessed = true;
        this.updateBtnText = 'Update';
        this.updateBtnError = false;
        this.cdr.detectChanges();
      }, 3500);
      return;
    }

    this.userGroupsService.UpdateGroup(group)
      .then(() => {
        this.getGroupList(undefined, false);
        this.updateBtnText = 'Update';
        this.isProcessed = true;
        this.isSaveSuccess = false;
        this.isUpdateSuccess = true;
        this.autoDismissMessages();
        this.cdr.detectChanges();
      })
      .catch((error: any) => {
        this.getGroupList(undefined, false);
        if (error.status === 409) {
          this.message = 'Record Already Exist.';
        } else {
          this.message = 'Error while updating group!! Try another Group name.';
        }
        this.isProcessed = true;
        this.updateBtnText = 'Oops! Something went wrong';
        this.updateBtnError = true;
        setTimeout(() => {
          this.updateBtnText = 'Update';
          this.updateBtnError = false;
          this.message = null;
          this.cdr.detectChanges();
        }, 3500);
      });
  }

  showconfirm(): void {
    this.isConfirmDelete = true;
  }

  removeGroup(group: any): void {
    this.userGroupsService.GetUsersByGroup(group.id)
      .then((response: any) => {
        const users = Array.isArray(response) ? response : (response.data || response);
        if (users.length >= 1) {
          this.isDeleteSuccess = false;
          this.isSaveSuccess = false;
          this.isUpdateSuccess = false;
          this.isConfirmDelete = false;
          this.message = 'Group cannot be deleted as it contains users';
          setTimeout(() => {
            this.message = null;
            this.cdr.detectChanges();
          }, 2500);
        } else {
          this.userGroupsService.DeleteGroup(group)
            .then(() => {
              this.getGroupList(undefined, false);
              this.isDeleteSuccess = true;
              this.isSaveSuccess = false;
              this.isUpdateSuccess = false;
              this.isConfirmDelete = false;
              this.cdr.detectChanges();
            })
            .catch((error: any) => {
              this.message = error?.data?.error || 'Error deleting group.';
              this.saveBtnError = true;
              setTimeout(() => {
                this.saveBtnError = false;
                this.message = null;
                this.cdr.detectChanges();
              }, 2500);
            });
        }
      });
  }

  // ========================================================================
  // GROUP USERS PANEL
  // ========================================================================

openUsersForm(groupId: string | number): void {
  if (groupId) {
    this.userGroupId = groupId;
    this.userGroupsService.GetUsersByGroup(this.userGroupId)
      .then((response: any) => {
        // Get the data array from the response
        const rawUsers = response.data || response;
        
        this.usersList = rawUsers;
        this.totalUserRecords = rawUsers.length;
        
        // Build assignedUser lookup - use user_id as key
        this.assignedUser = {};
        for (let user of rawUsers) {
          this.assignedUser[user.user_id] = user;
        }
        
        this.loadUsers();
        this.cdr.detectChanges();
      })
      .catch(() => { });
  }
  this.isShowUserGroup = true;
  this.isCreateGroup = true;
  this.userSortType = 'name';
  this.searchUser = '';
}

  closeUserForm(): void {
    this.isShowUserGroup = false;
    this.saveBtnText = 'Save';
    this.saveBtnError = false;
    this.isSaveSuccess = false;
    this.isUpdateSuccess = false;
    this.isDeleteSuccess = false;
  }

  loadUsers(): void {
    this.assignedUser = {};
    const users = JSON.parse(JSON.stringify(this.usersList));

    this.usersService.GetUserGraph(['id', 'name'])
      .then((response: any) => {
        this.linkUserList = Array.isArray(response) ? response : (response.data || response);
        for (let i = 0; i < users.length; i++) {
          if (this.assignedUser[users[i].user_id] === undefined) {
            this.assignedUser[users[i].user_id] = users[i];
          }
        }
        this.cdr.detectChanges();
      })
      .catch((error: any) => {
        console.error('Error loading users for group:', error);
      });
  }
getFilteredLinkUsers(): any[] {
  let filtered = this.linkUserList;
  
  const seenIds = new Set();
  const seenNames = new Set();

  filtered = filtered.filter(u => {
    // 1. Filter out duplicates by ID first
    if (seenIds.has(u.id)) return false;

    // 2. Filter out duplicates by User Name next
    const normalizedName = (u.name || '').trim().toLowerCase();
    if (seenNames.has(normalizedName)) return false;

    // Record both values if unique
    seenIds.add(u.id);
    seenNames.add(normalizedName);
    return true;
  });

  // 3. Apply search filter
  if (this.searchUser) {
    const term = this.searchUser.toLowerCase();
    filtered = filtered.filter((u: any) =>
      u.name && u.name.toLowerCase().includes(term)
    );
  }
  
  // 4. Sort alphabetically
  return [...filtered].sort((a: any, b: any) => {
    const nameA = (a.name || '').toLowerCase();
    const nameB = (b.name || '').toLowerCase();
    
    if (this.userSortReverse) {
      return nameA < nameB ? 1 : -1;
    } else {
      return nameA > nameB ? 1 : -1;
    }
  });
}

  toggleAddAndDeleteUser(userDetails: any, isAdd: boolean): void {
    if (userDetails && isAdd) {
      userDetails.user_id = userDetails.id;
      this.addUser(userDetails);
    } else {
      userDetails.user_id = userDetails.id;
      userDetails.group_id = this.userGroupId;
      this.deleteGroupUser(userDetails);
    }
  }

  addUser(userdetails: any): void {
    this.isUsersLoaded = false;
    this.userGroupsService.AddGroupUser(this.userGroupId, userdetails.user_id)
      .then(() => {
        this.usersList.push(userdetails);
        this.assignedUser[userdetails.user_id] = userdetails;
        userdetails.deleteMessage = '';
        userdetails.addMessage = 'User added.';
        this.isUsersLoaded = true;
        setTimeout(() => {
          userdetails.addMessage = '';
          this.cdr.detectChanges();
        }, 2500);
        this.cdr.detectChanges();
      })
      .catch(() => {
        this.isUsersLoaded = true;
        userdetails.deleteMessage = '';
        userdetails.addMessage = 'Error while adding user!';
        setTimeout(() => {
          userdetails.addMessage = '';
          this.cdr.detectChanges();
        }, 2500);
      });
  }

  deleteGroupUser(userdetail: any): void {
    this.userGroupsService.DeleteGroupUser(userdetail.group_id, userdetail.user_id)
      .then(() => {
        userdetail.addMessage = '';
        userdetail.deleteMessage = 'User removed.';
        const index = this.usersList.indexOf(userdetail);
        if (index > -1) this.usersList.splice(index, 1);
        delete this.assignedUser[userdetail.user_id];
        setTimeout(() => {
          userdetail.deleteMessage = '';
          this.cdr.detectChanges();
        }, 2500);
        this.cdr.detectChanges();
      })
      .catch(() => {
        userdetail.addMessage = '';
        userdetail.deleteMessage = 'Error while removing user!';
        setTimeout(() => {
          userdetail.deleteMessage = '';
          this.cdr.detectChanges();
        }, 2500);
      });
  }

isUserAssigned(user: any): boolean {
  // Compare user.id (from all users) with user_id (from assigned users)
  return !!this.assignedUser[user.id];
}
  // ========================================================================
  // GROUP CHANNELS PANEL
  // ========================================================================

  openChannelForm(): void {
    this.isShowUserChannel = true;
  }

  closeChannelForm(): void {
    this.isShowUserChannel = false;
    this.searchChannel = '';
  }

  // ========================================================================
  // UTILITY
  // ========================================================================

  autoDismissMessages(): void {
    setTimeout(() => {
      this.message = null;
      this.errorMessage = null;
      this.cdr.detectChanges();
    }, 3500);
  }
}
